import React, { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import {
  ASSIGNMENT_DETAILS_TABLE,
  isAssignmentPerfWidget,
  isSRLWidgetSummarizable,
  SRL_COMPONENT_TYPES,
  SRL_DETAILS_TABLE,
} from '../../../../constants';
import s from './SRLSummary.module.css';
import { useGetComponentAiSummary } from './hooks/useGetComponentAiSummary.js';
import { useGrsAiSummariesPreference } from '../../../../hooks/useGrsAiSummariesPreference.js';
import { convertMarkupToHtmlSafely } from './utils/markupUtils';
import { generateComponentAiSummary } from '../../../../../core/api';
import PropTypes from 'prop-types';
import moment from 'moment';

export const SRLSummary = ({
  widget,
  courseId,
  assignmentId,
  isSRLIntroSurvey,
}) => {
  const queryClient = useQueryClient();
  const [isRegenerating, setIsRegenerating] = useState(false);
  const { aiSummariesPreferenceEnabled } = useGrsAiSummariesPreference(
    courseId
  );

  const getResponsesComponentId = () => {
    let TABLE_CONSTANTS = ASSIGNMENT_DETAILS_TABLE || {};
    if (!isAssignmentPerfWidget(widget.widgetType)) {
      TABLE_CONSTANTS =
        SRL_DETAILS_TABLE[widget.widgetType][isSRLIntroSurvey || false] || {};
    }

    if (TABLE_CONSTANTS.RESPONSES_COLUMN_IDX == null) {
      return null;
    }

    const tableComponent = widget.components?.find(
      (component) => component.component_type === SRL_COMPONENT_TYPES.TABLE
    );

    return tableComponent?.component_id;
  };

  const responsesComponentId = getResponsesComponentId();
  const shouldFetchSummary =
    isSRLWidgetSummarizable(isSRLIntroSurvey, widget.widgetType) &&
    responsesComponentId != null &&
    aiSummariesPreferenceEnabled;

  const {
    surveyResultsSummaryData,
    isLoadingAiSummary,
  } = useGetComponentAiSummary(
    courseId,
    assignmentId,
    responsesComponentId,
    shouldFetchSummary
  );

  const handleRegenerate = async () => {
    try {
      setIsRegenerating(true);
      const { summary, updatedAt, error } = await generateComponentAiSummary(
        courseId,
        assignmentId,
        responsesComponentId
      );

      if (summary != null) {
        queryClient.setQueryData(
          ['componentAiSummary', courseId, assignmentId, responsesComponentId],
          (oldData) => ({
            ...oldData,
            summary,
            updatedAt,
          })
        );
      } else if (error) {
        console.error(error);
      }
    } finally {
      setIsRegenerating(false);
    }
  };

  const renderPlaceholderOrContent = () => {
    const { summary } = surveyResultsSummaryData || {};
    switch (true) {
      case isLoadingAiSummary || isRegenerating:
        return (
          <div className={s.skeletonContainer}>
            <div className={s.skeletonLine} />
            <div className={s.skeletonLine} />
            <div className={s.skeletonLine} />
            <div className={s.skeletonLine} />
          </div>
        );

      // Placeholder before Assignment due date
      case summary == null:
        return (
          <div className={s.summaryPlaceholder}>
            Generated summary of student responses will be available after the
            assignment due date.
          </div>
        );

      // Summary has already been generated
      default: {
        // Markup -> String HTML -> Sanitize -> Parse into React elements
        const parsedSanitizedHtml = convertMarkupToHtmlSafely(summary);
        return <div className={s.summaryContent}>{parsedSanitizedHtml}</div>;
      }
    }
  };

  const renderSummary = () => {
    const { summary, updatedAt } = surveyResultsSummaryData || {};
    const showTimestamp =
      !isRegenerating && summary != null && updatedAt != null;
    const formattedTimestamp = moment
      .utc(updatedAt)
      .local()
      .format('h:mma MMM D, YYYY');

    return (
      <div className={s.summaryBox}>
        <div className={s.header}>
          <div className={s.title}>&#x2728; Summary of Student Responses</div>
          {showTimestamp && (
            <div className={s.timestamp}>Generated {formattedTimestamp}</div>
          )}
          {!isRegenerating && (
            <button
              type="button"
              className={s.regenerateButton}
              onClick={handleRegenerate}
              disabled={isRegenerating}
              data-test-id="Insights_grs_summaries_regenerate_summary_btn"
            >
              <span className={s.regenerateIcon}>&#x21BB;</span>
              Regenerate Summary
            </button>
          )}
        </div>
        {renderPlaceholderOrContent()}
      </div>
    );
  };

  return shouldFetchSummary ? renderSummary() : null;
};

SRLSummary.propTypes = {
  widget: PropTypes.object.isRequired,
  courseId: PropTypes.string.isRequired,
  assignmentId: PropTypes.string.isRequired,
  isSRLIntroSurvey: PropTypes.bool.isRequired,
};
